import { Box, FormControl, Typography } from '@mui/material'
import {
    CalendarIcon,
    DateTimePicker,
    LocalizationProvider,
    renderTimeViewClock,
} from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'

interface DateTimeInputProps {
    id: string
    label: string
    value: string
    placeholder?: string
    onChange: (id: string, value: dayjs.Dayjs | null) => void
    errorMessage?: string
    required?: boolean
    isLegend?: boolean
    disabled?: boolean
    clearable?: boolean
    minDateTime?: string
    maxDateTime?: string
    dateFormat?: string
    size?: 'small' | 'medium'
    displayAM_PM?: boolean
    timezone?: string | null
}

const DateTimeInput_EntityPreview: React.FC<DateTimeInputProps> = (
    props: DateTimeInputProps,
) => {
    const {
        id,
        label,
        value,
        onChange,
        errorMessage = '',
        required,
        isLegend = false,
        disabled,
        clearable = false,
        minDateTime,
        maxDateTime,
        dateFormat,
        size,
        displayAM_PM = false,
        timezone,
    } = props

    const onChangeDate = (val: dayjs.Dayjs | null) => {
        onChange(id, val)
    }

    const onClearDateTime = () => {
        onChange(id, null)
    }
    return (
        <Box sx={{ width: '100%' }}>
            <FormControl fullWidth disabled={!!disabled}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    {isLegend ? null : (
                        <Typography sx={{ opacity: '0.7', fontSize: '14px' }}>
                            {label}
                            {required ? '*' : ''}
                        </Typography>
                    )}
                    <DateTimePicker
                        className="custom-date-picker"
                        label={isLegend ? label : ''}
                        ampm={displayAM_PM}
                        viewRenderers={{
                            hours: renderTimeViewClock,
                            minutes: renderTimeViewClock,
                        }}
                        sx={{ fontSize: '14px' }}
                        slotProps={{
                            textField: {
                                id: id + '-input',
                                inputProps: {
                                    'data-testid': id + '-input-data-id',
                                },
                                size: size ?? 'small',
                                error: errorMessage !== '',
                                sx: {
                                    '& div': {
                                        fontSize: '14px',
                                    },
                                    '& button': {
                                        padding: '5px',
                                    },
                                    '.clearButton': {
                                        display: 'none',
                                        padding: '0px',
                                    },
                                    '&:hover': {
                                        '.clearButton': {
                                            display: 'flex',
                                        },
                                    },
                                    '& legend > span': {
                                        opacity: 0,
                                        fontSize: '11px',
                                    },
                                    '& input, & span': {
                                        fontSize: '14px',
                                        opacity:
                                            value !== '' &&
                                            value !== 'MM/DD/YYYY HH:mm' &&
                                            value !== null
                                                ? '1'
                                                : '0.7',
                                    },
                                },
                                fullWidth: true,
                                helperText: errorMessage,
                            },
                            field: {
                                id: id + '-field',
                                clearable,
                                onClear: () => {
                                    onClearDateTime()
                                },
                            },
                            layout: {
                                sx: {
                                    '& .MuiDateCalendar-root': {
                                        maxHeight: '295px',
                                    },
                                    '& .MuiTimeClock-root': {
                                        maxHeight: '295px',
                                        justifyContent: 'center',
                                    },
                                },
                            },
                        }}
                        slots={{
                            openPickerIcon: (props) => (
                                <CalendarIcon
                                    {...props}
                                    id={id + '-calendar-open-icon'}
                                />
                            ),
                        }}
                        timezone={timezone || dayjs.tz.guess()}
                        value={value ? dayjs.utc(value) : null}
                        onChange={(newValue) => onChangeDate(newValue)}
                        format={dateFormat ?? 'MM/DD/YYYY HH:mm'}
                        disabled={!!disabled}
                        minDateTime={
                            minDateTime ? dayjs(minDateTime) : undefined
                        }
                        maxDateTime={
                            maxDateTime ? dayjs(maxDateTime) : undefined
                        }
                    />
                </LocalizationProvider>
            </FormControl>
        </Box>
    )
}

export default DateTimeInput_EntityPreview
